import { Button, Input, message } from 'antd'
import { isFinite, isEmpty, min } from 'lodash'
import cx from 'clsx'
import produce from 'immer'
import { createChildListViewComponent } from 'factories/ChildListView'
import entityNames from 'options/entityNames'
import { getUserPermission } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { isReadOnly } from 'helpers/formViews'
import Icon from 'elements/Icon'
import FormView from 'containers/Rfqs/Items/FormView'
import InputNumber from 'elements/InputNumber'
import ConvertToOrder from 'containers/Rfqs/Items/ConvertToOrder'
import Copy from 'containers/Rfqs/Items/Copy'

const getBestPrice = ({ record, suppliers = [] }) => {
  const supplierIds = suppliers.map((each) => each.supplierId)

  return min(
    (record?.supplierDetails ?? []).filter((x) => supplierIds.includes(x.supplierId) && x.price > 0).map((y) => y.price)
  )
}

const getBestSupplierIds = ({ suppliers = [], items = [] }) => {
  const totals = suppliers.map((supplier) =>
    items
      .map((each) => each.supplierDetails.find((one) => one.supplierId === supplier.supplierId))
      .map((each) => each?.price ?? 0)
      .reduce((acc, each) => acc + each, 0)
  )

  const best = min(totals.filter((each) => each > 0))

  return totals.map((each, index) => (each === best ? suppliers[index]?.supplierId : null)).filter(Boolean)
}

export default createChildListViewComponent({
  entityName: entityNames.rfqs,
  childName: 'rfqItems',
  createButtonTextId: 'addNonStockItem',
  createButtonLinkTarget: {
    formSize: 'md',
    formComponent: (props) => <FormView {...props} settingsType="rfq" />,
  },
  primaryLinkTarget: 'rfqItemRecord',
  allowCreate: (self) => false,
  allowDelete: (self) =>
    !isReadOnly(self) && !self.props.userIsInRoleSupplier && getUserPermission('Purchase') !== 'No',
  allowCustomize: (self) => true,
  allowSearching: (self) => true,
  extraRowActions: (self) => (
    <>
      {!isReadOnly(self) && self.props.userIsInRoleSupplier && (
        <Button
          onClick={async () => {
            try {
              await self.props.onChange(
                produce(self.props.items, (draft) => {
                  draft.forEach((item) => {
                    ;(item?.supplierDetails ?? []).forEach((each) => {
                      each.price = 0
                    })
                  })
                })
              )
            } catch (error) {
              console.warn(error)
            }
          }}
          disabled={isEmpty(self.state.selectedRowKeys)}
        >
          <Icon type="MoneyOff" />
          {t('clearPrices')}
        </Button>
      )}
      {!self.props.userIsInRoleDistributor &&
        !self.props.userIsInRoleSupplier &&
        getUserPermission('Purchase') !== 'No' && (
          <Copy
            parentRecord={self.props.parentRecord}
            parentHasUnsavedChanges={self.props.parentHasUnsavedChanges}
            selectedRowKeys={self.state.selectedRowKeys}
          />
        )}
      {(!isReadOnly(self) || self.props.parentRecord.status === 'Closed') &&
        !self.props.userIsInRoleSupplier &&
        getUserPermission('Orders') !== 'None' && (
          <Button
            onClick={() =>
              self.props.parentHasUnsavedChanges
                ? message.error(t('saveChangesFirst'))
                : self.props.parentRecord.status !== 'Closed'
                  ? message.error(t('rfqMustBeInClosedStatus'))
                  : self.setState({ convertToOrderVisible: true })
            }
            disabled={isEmpty(self.state.selectedRowKeys)}
          >
            <Icon type="ShoppingCart" />
            {t('convertToOrder')}
          </Button>
        )}
      {self.state.convertToOrderVisible && (
        <ConvertToOrder
          parentRecord={self.props.parentRecord}
          selectedRowKeys={self.state.selectedRowKeys}
          onCancel={() => self.setState({ convertToOrderVisible: false })}
          onCreateOrder={self.props.onCreateOrder}
          onCreatedOrderDrawerClose={() =>
            self.setState({ convertToOrderVisible: false }, self.props.onCreatedOrderDrawerClose)
          }
        />
      )}
    </>
  ),
  tableCellComponents: {
    quantity: (self, item) => (
      <InputNumber
        value={item.quantity}
        onChange={async (value) => {
          try {
            await self.props.onChange(
              produce(self.props.items, (draft) => {
                if (isFinite(value)) {
                  const row = draft.find((one) => one.id === item.id)
                  row.quantity = Math.max(value, 1)
                  row.populated = true // HACK: To avoid calling /populate
                }
              })
            )
          } catch (error) {
            console.warn(error)
          }
        }}
        min={1}
        readOnly={isReadOnly(self)}
        disabled={self.props.userIsInRoleSupplier}
      />
    ),
  },
  tableHeadGroups: (self) => {
    const suppliers = (self.props.parentRecord?.rfqSupplierItems ?? []).filter((each) => each.displaySupplier)

    return {
      price: () =>
        suppliers.map((each) => ({
          title: each.supplierName,
          key: `supplierDetails[${each.supplierPosition}].price`,
          dataIndex: `supplierDetails[${each.supplierPosition}].price`,
          width: 126,
          render: (_, record, index) => {
            try {
              const { items = [] } = self.props

              const { price, supplierId } = items
                .find((one) => one.id === record.id)
                .supplierDetails.find((one) => one.supplierId === each.supplierId)

              return (
                <div
                  className={cx({
                    'table-cell-success':
                      !self.props.userIsInRoleSupplier && getBestSupplierIds({ suppliers, items }).includes(supplierId),
                  })}
                >
                  <InputNumber
                    className={cx({
                      'form-field-success':
                        !self.props.userIsInRoleSupplier && getBestPrice({ record, suppliers }) === price,
                    })}
                    value={price}
                    onChange={async (value) => {
                      try {
                        await self.props.onChange(
                          produce(self.props.items, (draft) => {
                            draft
                              .find((one) => one.id === record.id)
                              .supplierDetails.find((one) => one.supplierId === each.supplierId).price = value
                          })
                        )
                      } catch (error) {
                        console.warn(error)
                      }
                    }}
                    min={0}
                    readOnly={isReadOnly(self)}
                    currency
                  />
                </div>
              )
            } catch (error) {
              console.warn(error)

              return <Input disabled />
            }
          },
        })),
    }
  },
})()
